import React, {
    useState,
    useEffect,
    useMemo,
    useCallback
} from 'react'
import {
    Dropdown
} from 'semantic-ui-react'
import PropTypes from "prop-types";
import _ from 'lodash'
import './styles.css'
import { onError, onWarning } from './messages';

const DropDown = React.forwardRef((props, ref) => {
    const defaultSpec = useMemo(()=>{
        return {}
    }, [])

    const {
        api,
        data,
        spec = defaultSpec,
        valueField,
        textField,
        imageField,
        descriptionField,
        labelField,
        value,
        onChange,
        onChangeItem,
        selectAll,
        filter,
        initial,
        selectFirst,
        onValueNotFound,
        itemClassName,
        sort,
        ...rest
    } = props

    const [options, setOptions] = useState([])
    const [_value, setValue] = useState(value)
    const [loading, setLoading] = useState(false)
    const [items, setItems] = useState()

    const hasValue = ()=>{
        return value != null && value !== undefined
    }


    useEffect(() => {
        if (hasValue()) {
            setValue(value)
        } else {
            setValue(rest.multiple ? [] : null)
        }
    }, [value])

    useEffect(() => {
        let canceled = false
        if ((api && spec) || data) {
            (async () => {
                try {
                    setLoading(true)
                    let s = {}
                    if (spec) {
                        _.forOwn(spec, (value, key) => {
                            if (_.isArray(value)) {
                                _.forEach(value, (value, index) => {
                                    s[`${key}[${index}]`] = value
                                });
                            } else {
                                s[key] = value
                            }
                        })
                    }

                    let result = data ? data : await api(s);
                    if (hasValue() && onValueNotFound && _.isEmpty(result)) {
                        onValueNotFound()
                    }
                    if (!canceled) {
                        setItems(sort? sort(result): result)
                    }

                } catch (error) {
                    error.status === 400?onWarning(error):onError(error)
                } finally {
                    if (!canceled){
                        setLoading(false)
                    }
                }
            })()
        } else {
            console.warn("Either api and spec or data are required")
        }
        return ()=>(canceled = true)
    }, [api, spec, data])

    useEffect(() => {
        if (items) {
            let result = [...items]
            if (initial && !_.find(result, r => r[valueField] === initial[valueField])) {
                result.push(initial)
            }

            let options = result.map(item => {
                let text = ""
                if (typeof (textField) === "function") {
                    text = textField(item)
                } else {
                    text = item[textField]
                }
                let description = null
                if (descriptionField) {
                    if (typeof (descriptionField) === "function") {
                        description = descriptionField(item)
                    } else {
                        description = item[descriptionField]
                    }
                }
                let label = null
                if (labelField) {
                    if (typeof (labelField) === "function") {
                        label = labelField(item)
                    } else {
                        label = {
                            horisontal: "true",
                            basic: true,
                            content: item[labelField]
                        }
                    }
                }

                let className = null
                if (itemClassName) {
                    if (typeof (itemClassName) === "function") {
                        className = itemClassName(item)
                    } else {
                        className = itemClassName
                    }
                }

                let image = null;
                if (imageField) {
                    if (typeof (imageField) === "function") {
                        image = imageField(item)
                    } else {
                        image = item[imageField]
                    }
                }

                return {
                    value: item[valueField],
                    text: text,
                    item: item,
                    key: item[valueField],
                    description: description,
                    className: className,
                    label: label ? label : null,
                    image: image ? {
                        width: 24,
                        height: 24,
                        wrapped: true,
                        src: image
                    } : null
                }
            })
            options = filter ? filter(options, value) : options

            if (hasValue() && options) {
                let valueExists = _.find(options, o => { return o.value === value })
                if (!valueExists && onValueNotFound) {
                    onValueNotFound()
                } else if (valueExists && onChangeItem) {
                    onChangeItem(valueExists.item)
                }
            }

            if (!hasValue() && selectFirst) {
                let firstResult = _.first(options)
                if (typeof(selectFirst) === "function") {
                  firstResult = selectFirst(options)
                }

                if (firstResult) {
                    setValue(firstResult.value)
                    if (onChange) {
                        onChange(firstResult.value)
                    }
                    if (onChangeItem) {
                        onChangeItem(firstResult.item)
                    }
                }
            }

            if (!hasValue() && rest.multiple && selectAll) {
                const val = options.map(x=>x.value)
                setValue(val)
                if (onChange) {
                    onChange(val)
                }
                if (onChangeItem) {
                    onChangeItem(val)
                }
            }
            setOptions(options)
        }
    }, [items, filter, initial])

    const onValueChange = useCallback((e, { value }) => {
        // Handle special case of clear value
        if (value === "") {
            setValue(value)
            onChange?.(value)
            onChangeItem?.(null);
        }
        else {
            if (Array.isArray(value)) {
                const selected = options.filter((o) => value.includes(o.value));
                setValue(selected?.map(o => o.value))
                onChange?.(selected?.map(o => o.value))
                onChangeItem?.(selected?.map(o => o.item));
            } else {
                const selected = options.find(o => o.value === value)
                if (selected) {
                    setValue(value)
                    onChange?.(value)
                    onChangeItem?.(selected.item);
                }
            }
        }
    }, [onChange, onChangeItem, options])

    const style = useMemo(() => {
        return {
            minWidth: 0
        }
    }, [])
    return <Dropdown ref={
        ref
    }
        selection loading={
            loading
        }
        options={
            options
        }
        value={
            _value
        }
        onChange={
            onValueChange
        }
        style={style}
        {...rest}
        compact
    />
})
DropDown.displayName = "DropDown"
DropDown.propTypes = {
    api: PropTypes.func,
    spec: PropTypes.object,
    valueField: PropTypes.string.isRequired,
    textField: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
    imageField: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    descriptionField: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    labelField: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    value: PropTypes.any,
    onChange: PropTypes.func,
    data: PropTypes.any,
    onChangeItem: PropTypes.func,
    filter: PropTypes.func,
    initial: PropTypes.any,
    selectFirst: PropTypes.any,
    onValueNotFound: PropTypes.func,
    itemClassName: PropTypes.any,
    selectAll: PropTypes.bool,
    sort: PropTypes.func
}
export default DropDown
